import { useSEO } from '../../../utils/seo';

export default function HomeMaintenanceScheduleBoiseHomeowners() {
  useSEO({
    title: 'Home Maintenance Schedule for Boise Homeowners | Seasonal Checklist',
    description: 'Complete seasonal home maintenance schedule for Boise and Treasure Valley homeowners. Monthly, quarterly, and annual tasks to protect your Idaho home investment.',
    keywords: 'home maintenance schedule Boise, seasonal home maintenance Idaho, Treasure Valley home care, Boise homeowner checklist, Idaho home maintenance guide',
    canonical: `${import.meta.env.VITE_SITE_URL}/blog/home-maintenance-schedule-boise-homeowners`,
    ogType: 'article',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: 'Home Maintenance Schedule for Boise Homeowners | Seasonal Checklist',
          description: 'Complete seasonal home maintenance schedule for Boise and Treasure Valley homeowners. Monthly, quarterly, and annual tasks to protect your Idaho home investment.',
          author: {
            '@type': 'Organization',
            name: 'Fix Right Services'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Fix Right Services',
            logo: {
              '@type': 'ImageObject',
              url: `${import.meta.env.VITE_SITE_URL}/images/logo.svg`
            }
          },
          datePublished: '2025-01-15',
          dateModified: '2025-01-15'
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: import.meta.env.VITE_SITE_URL
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${import.meta.env.VITE_SITE_URL}/blog`
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Home Maintenance Schedule for Boise Homeowners',
              item: `${import.meta.env.VITE_SITE_URL}/blog/home-maintenance-schedule-boise-homeowners`
            }
          ]
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What home maintenance tasks should I do monthly in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Monthly tasks include: test smoke and CO detectors, check HVAC filters (replace if needed), inspect plumbing for leaks, clean garbage disposal, test garage door safety features, and check for pest activity. These quick checks prevent major problems.'
              }
            },
            {
              '@type': 'Question',
              name: 'When should I winterize my Boise home?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Start winterizing in October before first freeze. Key tasks: drain sprinkler systems, insulate outdoor faucets, seal windows and doors, service furnace, clean gutters, and reverse ceiling fans. Idaho winters require thorough preparation to prevent freeze damage.'
              }
            },
            {
              '@type': 'Question',
              name: 'How often should I service my HVAC system in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Service HVAC twice yearly: spring (before cooling season) and fall (before heating season). Idaho\'s temperature extremes require well-maintained systems. Professional service costs $100-$200 but prevents $3,000-$8,000 emergency repairs.'
              }
            },
            {
              '@type': 'Question',
              name: 'What are the most important home maintenance tasks for Treasure Valley homeowners?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Critical tasks: HVAC maintenance (twice yearly), gutter cleaning (spring and fall), sprinkler winterization (October), fence inspection (spring), roof inspection (annually), and water heater flushing (annually). These prevent the most expensive repairs.'
              }
            },
            {
              '@type': 'Question',
              name: 'How much should I budget for annual home maintenance in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Budget 1-3% of home value annually. For a $400,000 Boise home, that\'s $4,000-$12,000 per year. This covers routine maintenance, minor repairs, and builds reserves for major replacements (roof, HVAC, water heater).'
              }
            },
            {
              '@type': 'Question',
              name: 'What maintenance tasks are specific to Idaho climate?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Idaho-specific tasks: sprinkler winterization (freeze protection), snow load monitoring on roofs, ice dam prevention, foundation crack monitoring (freeze-thaw cycles), and fence post checking (frost heave). Our climate requires extra attention to freeze-related issues.'
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-6 py-16">
        <header className="mb-12">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <a href="/" className="hover:text-teal-600 transition-colors">Home</a>
            <span>/</span>
            <a href="/blog" className="hover:text-teal-600 transition-colors">Blog</a>
            <span>/</span>
            <span className="text-gray-900">Home Maintenance Schedule</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Home Maintenance Schedule for Boise Homeowners | Seasonal Checklist
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <time dateTime="2025-01-15">January 15, 2025</time>
            <span>•</span>
            <span>12 min read</span>
          </div>
        </header>

        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Regular <strong>home maintenance</strong> protects your investment and prevents costly emergency repairs. This comprehensive <strong>seasonal maintenance schedule</strong> is specifically designed for <strong>Boise and Treasure Valley homeowners</strong>, accounting for Idaho's unique climate challenges including cold winters, hot summers, and freeze-thaw cycles that affect homes differently than other regions.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why Home Maintenance Matters in Idaho</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Idaho's climate presents unique challenges for homeowners:
          </p>
          <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
            <li><strong>Temperature extremes:</strong> From below 0°F in winter to 100°F+ in summer</li>
            <li><strong>Freeze-thaw cycles:</strong> Cause foundation cracks and pipe damage</li>
            <li><strong>Low humidity:</strong> Affects wood, paint, and seals</li>
            <li><strong>Irrigation systems:</strong> Require seasonal winterization</li>
            <li><strong>Snow and ice:</strong> Impact roofs, gutters, and drainage</li>
          </ul>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8">
            <p className="text-gray-700 font-semibold">
              <strong>Budget Rule:</strong> Set aside 1-3% of your home's value annually for maintenance. For a $400,000 Boise home, that's $4,000-$12,000 per year for routine upkeep and reserves.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Monthly Maintenance Tasks (Year-Round)</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            These quick checks take 30-60 minutes and prevent major problems:
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">Monthly Checklist:</h3>
            <ul className="space-y-2 text-gray-700">
              <li>☐ <strong>Test smoke and CO detectors</strong> (press test button)</li>
              <li>☐ <strong>Check HVAC filters</strong> (replace if dirty, typically every 1-3 months)</li>
              <li>☐ <strong>Inspect plumbing for leaks</strong> (under sinks, around toilets)</li>
              <li>☐ <strong>Run garbage disposal with ice and citrus</strong> (cleans and deodorizes)</li>
              <li>☐ <strong>Test garage door safety features</strong> (auto-reverse)</li>
              <li>☐ <strong>Check for pest activity</strong> (droppings, damage)</li>
              <li>☐ <strong>Clean range hood filter</strong> (dishwasher safe)</li>
              <li>☐ <strong>Inspect weather stripping</strong> (doors and windows)</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Spring Maintenance (March - May)</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Spring is the busiest maintenance season in Idaho. After winter, inspect for damage and prepare for summer heat.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Exterior Tasks</h3>
          <div className="space-y-4 mb-8">
            <div className="border-l-4 border-green-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Roof and Gutters</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Inspect roof for winter damage (missing shingles, ice damage)</li>
                <li>• Clean gutters and downspouts thoroughly</li>
                <li>• Check for proper drainage away from foundation</li>
                <li>• Trim tree branches away from roof (6+ feet clearance)</li>
              </ul>
            </div>

            <div className="border-l-4 border-green-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Foundation and Drainage</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Inspect foundation for new cracks (freeze-thaw damage)</li>
                <li>• Ensure soil slopes away from foundation (6 inches over 10 feet)</li>
                <li>• Check sump pump operation (pour water to test)</li>
                <li>• Clean window wells and ensure drainage</li>
              </ul>
            </div>

            <div className="border-l-4 border-green-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Irrigation and Landscaping</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Turn on sprinkler system and check all zones</li>
                <li>• Inspect for broken heads, leaks, or misaligned spray</li>
                <li>• Adjust timer for spring watering schedule</li>
                <li>• Mulch garden beds (2-3 inches depth)</li>
                <li>• Prune dead branches and shape shrubs</li>
              </ul>
            </div>

            <div className="border-l-4 border-green-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Fencing and Structures</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Inspect fence for winter damage (loose boards, leaning posts)</li>
                <li>• Check for frost heave on posts</li>
                <li>• Power wash and restain/reseal if needed</li>
                <li>• Inspect deck for loose boards, protruding nails</li>
              </ul>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Interior Tasks</h3>
          <div className="space-y-4 mb-8">
            <div className="border-l-4 border-blue-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">HVAC System</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Schedule professional AC service before summer</li>
                <li>• Replace air filters</li>
                <li>• Clean air vents and returns</li>
                <li>• Test AC operation on warm day</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Windows and Doors</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Clean windows inside and out</li>
                <li>• Check and replace damaged screens</li>
                <li>• Lubricate door hinges and locks</li>
                <li>• Test window locks and operation</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Safety Systems</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Replace smoke detector batteries</li>
                <li>• Test all smoke and CO detectors</li>
                <li>• Check fire extinguisher pressure gauges</li>
                <li>• Review emergency evacuation plan with family</li>
              </ul>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Summer Maintenance (June - August)</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Summer in Boise means heat stress on your home. Focus on cooling systems and outdoor spaces.
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">Summer Checklist:</h3>
            <ul className="space-y-2 text-gray-700">
              <li>☐ <strong>Monitor AC performance</strong> (should cool home efficiently)</li>
              <li>☐ <strong>Check attic ventilation</strong> (prevents heat buildup)</li>
              <li>☐ <strong>Inspect irrigation system weekly</strong> (adjust for heat)</li>
              <li>☐ <strong>Power wash exterior surfaces</strong> (siding, deck, driveway)</li>
              <li>☐ <strong>Inspect and clean dryer vent</strong> (fire prevention)</li>
              <li>☐ <strong>Check caulking around windows</strong> (prevents AC loss)</li>
              <li>☐ <strong>Inspect deck and fence stain</strong> (UV damage)</li>
              <li>☐ <strong>Clean refrigerator coils</strong> (improves efficiency)</li>
              <li>☐ <strong>Test sump pump monthly</strong> (if applicable)</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Fall Maintenance (September - November)</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Fall is critical for winterization in Idaho. Prepare your home for freezing temperatures.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Critical Fall Tasks</h3>
          <div className="space-y-4 mb-8">
            <div className="border-l-4 border-orange-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Winterization (Complete by October)</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• <strong>Drain sprinkler system</strong> (hire pro or use air compressor)</li>
                <li>• <strong>Insulate outdoor faucets</strong> (foam covers)</li>
                <li>• <strong>Disconnect and store garden hoses</strong></li>
                <li>• <strong>Shut off exterior water valves</strong></li>
                <li>• <strong>Drain outdoor faucets completely</strong></li>
              </ul>
            </div>

            <div className="border-l-4 border-orange-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Heating System</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Schedule professional furnace service</li>
                <li>• Replace furnace filter</li>
                <li>• Test heating system operation</li>
                <li>• Bleed radiators if applicable</li>
                <li>• Reverse ceiling fan direction (clockwise)</li>
              </ul>
            </div>

            <div className="border-l-4 border-orange-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Exterior Preparation</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Clean gutters thoroughly (after leaves fall)</li>
                <li>• Inspect roof for damage before snow</li>
                <li>• Seal cracks in driveway and walkways</li>
                <li>• Store outdoor furniture or cover securely</li>
                <li>• Check chimney and schedule cleaning if needed</li>
              </ul>
            </div>

            <div className="border-l-4 border-orange-600 pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Weatherproofing</h4>
              <ul className="space-y-1 text-gray-700">
                <li>• Caulk around windows and doors</li>
                <li>• Add weatherstripping where needed</li>
                <li>• Insulate attic access door</li>
                <li>• Check for drafts and seal gaps</li>
                <li>• Install door sweeps on exterior doors</li>
              </ul>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Winter Maintenance (December - February)</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Winter maintenance focuses on monitoring systems and preventing freeze damage.
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">Winter Checklist:</h3>
            <ul className="space-y-2 text-gray-700">
              <li>☐ <strong>Monitor heating system performance</strong> (consistent temps)</li>
              <li>☐ <strong>Check for ice dams on roof</strong> (remove if forming)</li>
              <li>☐ <strong>Keep gutters clear of ice</strong></li>
              <li>☐ <strong>Monitor indoor humidity</strong> (30-50% ideal)</li>
              <li>☐ <strong>Check for frozen pipes</strong> (especially exterior walls)</li>
              <li>☐ <strong>Clear snow from HVAC units</strong></li>
              <li>☐ <strong>Test sump pump if temperatures allow</strong></li>
              <li>☐ <strong>Inspect attic for ice dam damage</strong></li>
              <li>☐ <strong>Keep cabinet doors open during extreme cold</strong> (prevents pipe freezing)</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Annual Maintenance Tasks</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Complete these tasks once per year, scheduling during the recommended season:
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Task</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Best Season</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Professional roof inspection</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Spring or Fall</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$150-$300</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Chimney inspection and cleaning</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Fall</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$150-$300</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Water heater flush and inspection</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Spring</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$100-$200</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Septic system inspection (if applicable)</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Spring or Summer</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$200-$400</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Dryer vent professional cleaning</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Any</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$100-$200</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Deck/fence staining or sealing</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Spring or Summer</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$500-$2,000</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Garage door service and lubrication</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Spring or Fall</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$100-$150</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Pressure wash exterior</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Summer</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$200-$500</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Every 3-5 Years</h2>
          <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
            <li><strong>Exterior painting:</strong> $3,000-$8,000 (protects siding)</li>
            <li><strong>Carpet cleaning:</strong> $150-$400 (extends carpet life)</li>
            <li><strong>Driveway sealing:</strong> $300-$800 (prevents cracking)</li>
            <li><strong>Window caulking replacement:</strong> $200-$600 (energy efficiency)</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Major Replacements (Plan Ahead)</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Item</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Lifespan</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Replacement Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Roof (asphalt shingles)</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">20-25 years</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$8,000-$15,000</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">HVAC system</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">15-20 years</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$5,000-$10,000</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Water heater</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">10-15 years</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$1,200-$2,500</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Fence (wood)</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">15-20 years</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$5,000-$8,000</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Garage door</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">15-30 years</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$1,000-$3,000</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Windows</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">20-30 years</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">$5,000-$15,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Idaho-Specific Maintenance Priorities</h2>
          <div className="space-y-6 mb-8">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3">Freeze Protection (Critical)</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Sprinkler winterization by mid-October (prevents $2,000-$5,000 repairs)</li>
                <li>• Outdoor faucet insulation (prevents burst pipes)</li>
                <li>• Maintain heat in all areas of home (prevents frozen pipes)</li>
                <li>• Monitor weather forecasts for extreme cold</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3">Irrigation System Care</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Spring startup inspection (check for winter damage)</li>
                <li>• Mid-summer efficiency check (adjust for heat)</li>
                <li>• Fall winterization (professional blowout recommended)</li>
                <li>• Controller battery replacement annually</li>
              </ul>
            </div>

            <div className="bg-orange-50 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-3">Foundation Monitoring</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Spring inspection after freeze-thaw cycles</li>
                <li>• Monitor existing cracks for growth</li>
                <li>• Maintain proper drainage away from foundation</li>
                <li>• Address cracks promptly (prevents water intrusion)</li>
              </ul>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Maintenance Record Keeping</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Keep detailed records of all maintenance and repairs:
          </p>
          <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
            <li><strong>Create a home maintenance binder:</strong> Store receipts, warranties, manuals</li>
            <li><strong>Document dates and costs:</strong> Track when tasks were completed</li>
            <li><strong>Take photos:</strong> Before and after major work</li>
            <li><strong>Save contractor information:</strong> For future reference</li>
            <li><strong>Note product details:</strong> Paint colors, materials used, model numbers</li>
            <li><strong>Track warranty periods:</strong> Set reminders before expiration</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">When to Call a Professional</h2>
          <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-3">Don't DIY These Tasks:</h3>
            <ul className="space-y-2 text-gray-700">
              <li>🔧 <strong>HVAC service:</strong> Requires specialized tools and knowledge</li>
              <li>🔧 <strong>Electrical work:</strong> Safety risk and code requirements</li>
              <li>🔧 <strong>Roof repairs:</strong> Height danger and warranty concerns</li>
              <li>🔧 <strong>Sprinkler winterization:</strong> Improper blowout causes damage</li>
              <li>🔧 <strong>Gas line work:</strong> Explosion risk</li>
              <li>🔧 <strong>Structural issues:</strong> Requires engineering assessment</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6 mb-12">
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What home maintenance tasks should I do monthly in Boise?</h3>
              <p className="text-gray-700">Monthly tasks include: test smoke and CO detectors, check HVAC filters (replace if needed), inspect plumbing for leaks, clean garbage disposal, test garage door safety features, and check for pest activity. These quick checks prevent major problems.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">When should I winterize my Boise home?</h3>
              <p className="text-gray-700">Start winterizing in October before first freeze. Key tasks: drain sprinkler systems, insulate outdoor faucets, seal windows and doors, service furnace, clean gutters, and reverse ceiling fans. Idaho winters require thorough preparation to prevent freeze damage.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How often should I service my HVAC system in Idaho?</h3>
              <p className="text-gray-700">Service HVAC twice yearly: spring (before cooling season) and fall (before heating season). Idaho's temperature extremes require well-maintained systems. Professional service costs $100-$200 but prevents $3,000-$8,000 emergency repairs.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What are the most important home maintenance tasks for Treasure Valley homeowners?</h3>
              <p className="text-gray-700">Critical tasks: HVAC maintenance (twice yearly), gutter cleaning (spring and fall), sprinkler winterization (October), fence inspection (spring), roof inspection (annually), and water heater flushing (annually). These prevent the most expensive repairs.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How much should I budget for annual home maintenance in Boise?</h3>
              <p className="text-gray-700">Budget 1-3% of home value annually. For a $400,000 Boise home, that's $4,000-$12,000 per year. This covers routine maintenance, minor repairs, and builds reserves for major replacements (roof, HVAC, water heater).</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What maintenance tasks are specific to Idaho climate?</h3>
              <p className="text-gray-700">Idaho-specific tasks: sprinkler winterization (freeze protection), snow load monitoring on roofs, ice dam prevention, foundation crack monitoring (freeze-thaw cycles), and fence post checking (frost heave). Our climate requires extra attention to freeze-related issues.</p>
            </div>
          </div>

          <div className="bg-teal-600 text-white rounded-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Need Help With Home Maintenance?</h2>
            <p className="text-xl mb-6">
              Fix Right Services handles all your home maintenance needs in Boise and the Treasure Valley. From seasonal tasks to emergency repairs, we've got you covered.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Schedule Maintenance Service
            </a>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Related Articles:</h3>
            <ul className="space-y-2">
              <li>
                <a href="/blog/seasonal-home-maintenance-treasure-valley" className="text-teal-600 hover:underline">
                  Seasonal Home Maintenance Tips for Treasure Valley
                </a>
              </li>
              <li>
                <a href="/blog/top-home-repairs-boise-homeowners-ignore" className="text-teal-600 hover:underline">
                  Top Home Repairs Boise Homeowners Ignore
                </a>
              </li>
              <li>
                <a href="/blog/handyman-services-cost-boise-idaho" className="text-teal-600 hover:underline">
                  Handyman Services Cost in Boise, Idaho
                </a>
              </li>
            </ul>
          </div>
        </div>
      </article>
    </div>
  );
}